/* =========================================================================
   JOMIKUTO · product-form.js
   <jomi-product-form> custom element wrapping a <form>.
   Intercepts submit, POSTs to /cart/add.js, updates cart state, opens drawer.
   ========================================================================= */

class JomiProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.submitButton = this.querySelector('[type="submit"]');
    this.errorEl = this.querySelector('[data-jomi-form-error]');
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitButton?.hasAttribute('disabled')) return;

    // Instant feedback: label + state changes fire synchronously BEFORE the
    // network round-trip, giving users immediate acknowledgment of their click.
    // This is the reliable integration point — no capture-phase race, no disabled-button
    // form-submission trap. The button's own click already triggered the submit event
    // by the time we get here, so setting state now is safe.
    this.#setInstantFeedback();

    this.#clearError();
    this.#setLoading(true);

    try {
      const formData = new FormData(this.form);
      const result = await Jomi.cart.add(formData);
      const cart = await Jomi.cart.get();
      Jomi.bus.emit('cart:updated', cart);
      Jomi.bus.emit('cart:itemAdded', { item: result, cart });

      // Feedback strategy:
      //   Update bag count (handled by cart:updated event bus) + show inline toast.
      //   We do NOT auto-open the cart drawer — the user should stay where they
      //   are so they can continue shopping. They'll see the bag counter go up
      //   in the header, and can open the drawer/cart when ready.
      this.#flashAddedState();
      if (window.Jomi && Jomi.cart && typeof Jomi.cart.showAddedToast === 'function') {
        Jomi.cart.showAddedToast(result);
      }
    } catch (err) {
      this.#showError(err.message || 'Something went wrong. Please try again.');
      this.#revertLabel();
    } finally {
      this.#setLoading(false);
    }
  }

  #setInstantFeedback() {
    if (!this.submitButton) return;
    // Remember original label once so revert always works
    if (this._originalLabel === undefined) {
      const labelEl = this.submitButton.querySelector('[data-jomi-atc-label]') || this.submitButton.querySelector('span') || this.submitButton;
      this._labelEl = labelEl;
      this._originalLabel = labelEl.textContent;
    }
    this._labelEl.textContent = 'Adding…';
    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton.classList.add('is-loading');
    // Intentionally NOT setting disabled=true — would break form submission if
    // called from onSubmit (browser re-evaluates form validity). Use CSS pointer-events
    // via .is-loading class to block double-clicks instead.
  }

  #flashAddedState() {
    if (!this.submitButton || !this._labelEl) return;
    this._labelEl.textContent = 'Added ✓';
    this.submitButton.classList.add('is-added');
    clearTimeout(this._revertTimer);
    this._revertTimer = setTimeout(() => {
      this.#revertLabel();
      this.submitButton.classList.remove('is-added');
    }, 900);
  }

  #revertLabel() {
    if (!this._labelEl || this._originalLabel === undefined) return;
    this._labelEl.textContent = this._originalLabel;
  }

  #setLoading(isLoading) {
    if (!this.submitButton) return;
    if (isLoading) {
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.classList.add('is-loading');
    } else {
      this.submitButton.removeAttribute('aria-busy');
      this.submitButton.classList.remove('is-loading');
    }
  }

  #showError(msg) {
    if (!this.errorEl) {
      console.error('[JomiProductForm]', msg);
      return;
    }
    this.errorEl.textContent = msg;
    this.errorEl.removeAttribute('hidden');
  }

  #clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.setAttribute('hidden', '');
  }
}

if (!customElements.get('jomi-product-form')) {
  customElements.define('jomi-product-form', JomiProductForm);
}
